import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { User, Briefcase, Eye, Loader2, Save, Bell, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageUpload from "@/components/ImageUpload";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import { HomeCenterPicker } from "@/components/member/HomeCenterPicker";

const VIS_LABELS = {
  public: "Public", members: "All members", community: "Community+",
  enhancers: "Enhancers+", managers: "Managers+", admin: "Admin only", private: "Private",
};
const ROLE_PREVIEW = [
  { v: "visitor", l: "Guest" }, { v: "discovery", l: "Discovery Member" },
  { v: "community", l: "Community Member" }, { v: "enhancer", l: "Enhancer" },
  { v: "location_manager", l: "Location Manager" }, { v: "admin", l: "Admin" },
];
const BUSINESS_ROLES = ["enhancer", "vendor", "speaker", "musician", "location_manager"];

// Module-scope field components: defining these inside the page component
// recreated their type every keystroke, remounting the input and collapsing
// the mobile keyboard.
const VisSelect = ({ k, controllable, effVis, setVis, levels }) => controllable.includes(k) ? (
    <select value={effVis(k)} onChange={(e) => setVis(k, e.target.value)} data-testid={`vis-${k}`}
      className="eyw-input !py-1.5 !px-2 text-xs !w-auto shrink-0 bg-[#F9F7F3]">
      {levels.map((l) => <option key={l} value={l}>{VIS_LABELS[l] || l}</option>)}
    </select>
  ) : <span className="text-[10px] text-[#8FA89B] uppercase tracking-wide shrink-0">—</span>;

const Field = ({ k, label, span, placeholder, visKey, f, set, controllable, effVis, setVis, levels }) => (
    <div className={span ? "sm:col-span-2" : ""}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="text-xs font-medium text-[#4A5D6B]">{label}</label>
        {(controllable.includes(visKey || k)) && <VisSelect k={visKey || k} controllable={controllable} effVis={effVis} setVis={setVis} levels={levels} />}
      </div>
      <input value={f[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={placeholder || ""}
        data-testid={`profile-${k}`} className="eyw-input w-full text-sm" />
    </div>
  );

export default function ProfileSettings() {
  const { user } = useAuth();
  const { data, refetch } = useQuery({
    queryKey: ["my-full-profile"], enabled: !!user,
    queryFn: () => api.get("/me/profile/full").then((r) => r.data),
  });
  const [f, setF] = useState({});
  const [fv, setFv] = useState({});
  const [levels, setLevels] = useState([]);
  const [controllable, setControllable] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewRole, setPreviewRole] = useState("visitor");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!data) return;
    setF({ ...data, specialties: (data.specialties || []).join(", ") });
    setFv(data.field_visibility || {});
    setLevels(data.visibility_levels || []);
    setControllable(data.controllable_fields || []);
    setDefaults(data.default_visibility || {});
  }, [data]);

  if (!user) return <div className="max-w-2xl mx-auto px-6 py-24 text-center text-[#4A5D6B]">Please sign in to manage your profile.</div>;

  const showBusiness = BUSINESS_ROLES.includes(user.role) || !!user.is_enhancer;
  const isEnhancer = user.role === "enhancer" || !!user.is_enhancer;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setVis = (k, v) => setFv((p) => ({ ...p, [k]: v }));
  const effVis = (k) => fv[k] || defaults[k] || "members";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        specialties: (f.specialties || "").split(",").map((s) => s.trim()).filter(Boolean),
        field_visibility: fv,
      };
      delete payload.email; delete payload.id; delete payload.role;
      delete payload.controllable_fields; delete payload.visibility_levels; delete payload.default_visibility;
      await api.put("/me/profile/full", payload);
      toast.success("Profile saved");
      refetch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runPreview = async (role) => {
    setPreviewRole(role);
    try {
      const r = await api.get(`/me/profile/preview?as_role=${role}`);
      setPreview(r.data);
    } catch (e) { toast.error("Could not load preview"); }
  };

  const fp = { f, set, controllable, effVis, setVis, levels };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="profile-settings">
      <div className="flex items-center gap-2 mb-2"><User size={24} className="text-[#144F75]" /><h1 className="font-serif text-4xl text-[#1A242B]">Profile &amp; Privacy</h1></div>
      <p className="text-[#4A5D6B] mb-10">Add your details and choose exactly who can see each one. Personal contact details default to private.</p>

      {isEnhancer && (
        <section className="eyw-card p-6 mb-8 border-[#9E5C7F]/30 bg-[#9E5C7F]/[0.04]" data-testid="profile-enhancer-entry">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><Sparkles size={18} className="text-[#9E5C7F]" /> Your enhancer profile</h2>
              <p className="text-sm text-[#4A5D6B] mt-1">Manage your public photo, bio, offerings, availability, location and certifications in your Enhancer Dashboard.</p>
            </div>
            <Link to="/enhancer" data-testid="profile-open-enhancer-dashboard" className="eyw-btn !py-2.5 shrink-0">
              Open Enhancer Dashboard <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      {/* Personal */}
      <section className="eyw-card p-8 mb-8" data-testid="profile-personal">
        <h2 className="font-serif text-2xl text-[#1A242B] mb-6 flex items-center gap-2"><User size={20} /> Personal contact</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field {...fp} k="first_name" label="First name" />
          <Field {...fp} k="last_name" label="Last name" />
          <Field {...fp} k="preferred_name" label="Preferred name" />
          <Field {...fp} k="display_name" label="Display name" />
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="text-xs font-medium text-[#4A5D6B]">Profile photo</label>
              {controllable.includes("avatar") && <VisSelect {...fp} k="avatar" />}
            </div>
            <ImageUpload value={f.avatar || ""} onChange={(url) => set("avatar", url)} label="" rounded testid="profile-avatar" />
          </div>
          <Field {...fp} k="phone" label="Phone" visKey="phone" />
          <Field {...fp} k="alt_phone" label="Alternate phone" visKey="alt_phone" />
          <Field {...fp} k="address" label="Mailing address" visKey="address" span />
          <Field {...fp} k="city" label="City" visKey="city_state" />
          <Field {...fp} k="state" label="State / Region" visKey="city_state" />
          <Field {...fp} k="zip" label="ZIP / Postal code" />
          <Field {...fp} k="country" label="Country" />
          <Field {...fp} k="timezone" label="Time zone" visKey="timezone" />
          <Field {...fp} k="preferred_contact" label="Preferred contact method" visKey="preferred_contact" />
          <Field {...fp} k="emergency_contact" label="Emergency contact" visKey="emergency_contact" span />
        </div>
      </section>

      <HomeCenterPicker />

      {/* Business */}
      {showBusiness && (
        <section className="eyw-card p-8 mb-8" data-testid="profile-business">
          <h2 className="font-serif text-2xl text-[#1A242B] mb-6 flex items-center gap-2"><Briefcase size={20} /> Professional &amp; business</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field {...fp} k="professional_title" label="Professional title" visKey="professional_title" />
            <Field {...fp} k="business_name" label="Business name" visKey="business_name" />
            <Field {...fp} k="business_email" label="Business email" visKey="business_email" />
            <Field {...fp} k="business_phone" label="Business phone" visKey="business_phone" />
            <Field {...fp} k="website" label="Website" visKey="website" span />
            <Field {...fp} k="business_address" label="Business address" visKey="business_address" span />
            <Field {...fp} k="service_areas" label="Service areas" visKey="service_areas" />
            <Field {...fp} k="credentials" label="Credentials / licenses" visKey="credentials" />
            <Field {...fp} k="specialties" label="Specialties (comma separated)" visKey="specialties" span />
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="text-xs font-medium text-[#4A5D6B]">Bio</label><VisSelect {...fp} k="bio" />
              </div>
              <RichTextEditor value={f.bio || ""} onChange={(v) => set("bio", v)} testid="profile-bio" placeholder="Tell your story — add headings, images and video…" minHeight={160} />
            </div>
          </div>
        </section>
      )}

      {/* Communication & directory */}
      <section className="eyw-card p-8 mb-8" data-testid="profile-comms">
        <h2 className="font-serif text-2xl text-[#1A242B] mb-4 flex items-center gap-2"><Bell size={20} /> Communication &amp; directory</h2>
        <label className="flex items-center justify-between py-2.5 border-b border-[#E5E0D8]">
          <span className="text-sm text-[#1A242B]">Show me in the member directory</span>
          <input type="checkbox" checked={f.show_in_directory !== false} onChange={(e) => set("show_in_directory", e.target.checked)} data-testid="profile-directory" className="accent-[#144F75]" />
        </label>
        <label className="flex items-center justify-between py-2.5 border-b border-[#E5E0D8]">
          <span className="text-sm text-[#1A242B]">Email updates</span>
          <input type="checkbox" checked={!!f.email_opt_in} onChange={(e) => set("email_opt_in", e.target.checked)} data-testid="profile-email-opt" className="accent-[#144F75]" />
        </label>
        <label className="flex items-center justify-between py-2.5">
          <span className="text-sm text-[#1A242B]">SMS reminders</span>
          <input type="checkbox" checked={!!f.sms_opt_in} onChange={(e) => set("sms_opt_in", e.target.checked)} data-testid="profile-sms-opt" className="accent-[#144F75]" />
        </label>

        <div className="mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="notif-prefs">
          <h3 className="text-sm font-semibold text-[#1A242B] mb-1">Which alerts reach your bell?</h3>
          <p className="text-xs text-[#8FA89B] mb-3">Turn off anything you'd rather not be notified about. Everything is on by default.</p>
          <div className="grid sm:grid-cols-2 gap-x-6">
            {[{ k: "like", l: "Likes on my posts" }, { k: "comment", l: "Comments on my posts" }, { k: "message", l: "Direct messages" }, { k: "event", l: "New group gatherings" }, { k: "payout", l: "Payouts received" }].map((o) => (
              <label key={o.k} className="flex items-center justify-between py-2 border-b border-[#E5E0D8]/60">
                <span className="text-sm text-[#4A5D6B]">{o.l}</span>
                <input type="checkbox" checked={(f.notif_prefs || {})[o.k] !== false}
                  onChange={(e) => set("notif_prefs", { ...(f.notif_prefs || {}), [o.k]: e.target.checked })}
                  data-testid={`notif-pref-${o.k}`} className="accent-[#144F75]" />
              </label>
            ))}
          </div>
        </div>
      </section>

      <div className="flex justify-end mb-12">
        <button onClick={save} disabled={saving} data-testid="profile-save" className="eyw-btn disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save profile
        </button>
      </div>

      {/* Preview */}
      <section className="eyw-card p-8" data-testid="profile-preview-section">
        <h2 className="font-serif text-2xl text-[#1A242B] mb-2 flex items-center gap-2"><Eye size={20} /> Preview my profile as…</h2>
        <p className="text-sm text-[#4A5D6B] mb-4">See exactly which details are visible to each type of viewer. Save first to preview your latest changes.</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {ROLE_PREVIEW.map((r) => (
            <button key={r.v} onClick={() => runPreview(r.v)} data-testid={`preview-role-${r.v}`}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${previewRole === r.v && preview ? "bg-[#144F75] text-white" : "bg-white border border-[#144F75]/10 text-[#4A5D6B] hover:border-[#144F75]/40"}`}>
              {r.l}
            </button>
          ))}
        </div>
        {preview ? (
          <div className="bg-[#F9F7F3] rounded-2xl p-6 border border-[#E5E0D8]" data-testid="profile-preview-result">
            <p className="text-xs uppercase tracking-wider text-[#8FA89B] mb-3">Visible to a {ROLE_PREVIEW.find((r) => r.v === previewRole)?.l}</p>
            <h3 className="font-serif text-xl text-[#1A242B]">{preview.name || "(no display name)"}</h3>
            {preview.headline && <p className="text-sm text-[#4A5D6B]">{preview.headline}</p>}
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
              {["phone", "email", "city", "business_name", "business_email", "business_phone", "website", "professional_title", "credentials", "service_areas", "bio"].map((k) =>
                preview[k] ? (
                  <div key={k} className="flex gap-2"><dt className="text-[#8FA89B] capitalize">{k.replace(/_/g, " ")}:</dt><dd className="text-[#1A242B]">{String(preview[k])}</dd></div>
                ) : null
              )}
            </dl>
            {(!preview.visible_fields || preview.visible_fields.length === 0) && (
              <p className="text-sm text-[#4A5D6B] italic mt-2">Only your name and headline are visible to this viewer.</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-[#8FA89B]">Choose a viewer above to see the preview.</p>
        )}
      </section>
    </div>
  );
}
